import { TimeRange } from "@/core/components/ui/time-range-picker";
import { dayjs } from "@/core/utils/date";
import { FORMAT_FILENAME_DATE } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import {
  EnrichedSpiffApproval,
  EnrichedSpiffApprovalUser,
} from "@/spiff-approvals/spiff-approval";
import { isEnrichedSpiffApprovalUser } from "./type-guards";

export interface UserCsvData {
  userId: string;
  userName: string;
  signupCount: number;
  referralCreationCount: number;
  referralCompletionCount: number;
  fiveStarReviewCount: number;
  yardSignPhotoCount: number;
  pricebookItemSaleCount: number;
  techGeneratedLeadCount: number;
  drivingScoreCount: number;
  totalAmount: number;
}

/**
 * Column configuration for CSV export
 * Each column has a header, data accessor, formatter, and visibility rules
 */
interface CsvColumnConfig {
  header: string;
  accessor: (user: UserCsvData) => string | number;
  formatter?: (value: string | number) => string;
  showInSpiffOnly: boolean;
}

/**
 * Column definitions for CSV export
 * Organized as a list to maintain order and enable functional filtering
 */
const CSV_COLUMNS: readonly CsvColumnConfig[] = [
  {
    header: "User Name",
    accessor: (user) => user.userName,
    showInSpiffOnly: true,
  },
  {
    header: "Total Amount",
    accessor: (user) => user.totalAmount,
    formatter: (value) => formatMoney(Number(value)) || "$0",
    showInSpiffOnly: true,
  },
  {
    header: "Signup Spiffs",
    accessor: (user) => user.signupCount,
    formatter: (value) => String(value),
    showInSpiffOnly: false,
  },
  {
    header: "New Referral Spiffs",
    accessor: (user) => user.referralCreationCount,
    formatter: (value) => String(value),
    showInSpiffOnly: false,
  },
  {
    header: "Closed Referral Spiffs",
    accessor: (user) => user.referralCompletionCount,
    formatter: (value) => String(value),
    showInSpiffOnly: false,
  },
  {
    header: "Five Star Review Spiffs",
    accessor: (user) => user.fiveStarReviewCount,
    formatter: (value) => String(value),
    showInSpiffOnly: true,
  },
  {
    header: "Yard Sign Spiffs",
    accessor: (user) => user.yardSignPhotoCount,
    formatter: (value) => String(value),
    showInSpiffOnly: true,
  },
  {
    header: "Pricebook Sale Spiffs",
    accessor: (user) => user.pricebookItemSaleCount,
    formatter: (value) => String(value),
    showInSpiffOnly: true,
  },
  {
    header: "Tech Generated Lead Spiffs",
    accessor: (user) => user.techGeneratedLeadCount,
    formatter: (value) => String(value),
    showInSpiffOnly: true,
  },
  {
    header: "Driving Score Spiffs",
    accessor: (user) => user.drivingScoreCount,
    formatter: (value) => String(value),
    showInSpiffOnly: true,
  },
] as const;

/**
 * Filters columns based on business type
 * @param isSpiffOnly - Whether the business is spiff-only
 * @returns Filtered list of column configurations
 */
const getVisibleColumns = (isSpiffOnly: boolean): readonly CsvColumnConfig[] =>
  CSV_COLUMNS.filter((col) => col.showInSpiffOnly || !isSpiffOnly);

export const filterApprovalsByTimeRange = (
  approvals: (EnrichedSpiffApproval | EnrichedSpiffApprovalUser)[],
  timeRange?: TimeRange | null,
): (EnrichedSpiffApproval | EnrichedSpiffApprovalUser)[] => {
  if (!timeRange) {
    return approvals;
  }

  return approvals.filter(
    (approval: EnrichedSpiffApproval | EnrichedSpiffApprovalUser) => {
      const displayDate = dayjs(approval.display_date);
      return (
        displayDate.isSameOrAfter(dayjs(timeRange.from)) &&
        displayDate.isSameOrBefore(dayjs(timeRange.to).endOf("day"))
      );
    },
  );
};

export const generateCsvFilename = (timeRange?: TimeRange | null): string => {
  if (!timeRange) {
    return `spiff-payments-${dayjs().format(FORMAT_FILENAME_DATE)}.csv`;
  }

  const fromFormatted = dayjs(timeRange.from).format(FORMAT_FILENAME_DATE);
  const toFormatted = dayjs(timeRange.to).format(FORMAT_FILENAME_DATE);

  if (fromFormatted === toFormatted) {
    return `spiff-payments-${fromFormatted}.csv`;
  }

  return `spiff-payments-${fromFormatted}-to-${toFormatted}.csv`;
};

// Helper function to process a single user approval and add it to the map
const processUserApproval = (
  userApproval: EnrichedSpiffApprovalUser,
  userDataMap: Map<string, UserCsvData>,
): void => {
  const userId = userApproval.user_id;

  // Get existing user data or create new entry
  const existingData = userDataMap.get(userId);

  if (existingData) {
    // Aggregate counts and amounts for existing user
    existingData.signupCount += userApproval.signup_count;
    existingData.referralCreationCount += userApproval.referral_creation_count;
    existingData.referralCompletionCount +=
      userApproval.referral_completion_count;
    existingData.fiveStarReviewCount += userApproval.five_star_review_count;
    existingData.yardSignPhotoCount += userApproval.yard_sign_photo_count;
    existingData.pricebookItemSaleCount +=
      userApproval.pricebook_item_sale_count;
    existingData.techGeneratedLeadCount +=
      userApproval.tech_generated_lead_count;
    existingData.drivingScoreCount += userApproval.driving_score_count;
    existingData.totalAmount += userApproval.total_amount;
  } else {
    // Create new user entry
    userDataMap.set(userId, {
      userId: userApproval.user_id,
      userName: userApproval.display_identification || "Deleted User",
      signupCount: userApproval.signup_count,
      referralCreationCount: userApproval.referral_creation_count,
      referralCompletionCount: userApproval.referral_completion_count,
      fiveStarReviewCount: userApproval.five_star_review_count,
      yardSignPhotoCount: userApproval.yard_sign_photo_count,
      pricebookItemSaleCount: userApproval.pricebook_item_sale_count,
      techGeneratedLeadCount: userApproval.tech_generated_lead_count,
      drivingScoreCount: userApproval.driving_score_count,
      totalAmount: userApproval.total_amount,
    });
  }
};

export const convertApprovalsToUserCsvData = (
  approvals: (EnrichedSpiffApproval | EnrichedSpiffApprovalUser)[],
): UserCsvData[] => {
  // Map to store aggregated data per user
  const userDataMap = new Map<string, UserCsvData>();

  approvals.forEach((approval) => {
    // Handle single-user approvals (flattened structure)
    if (isEnrichedSpiffApprovalUser(approval)) {
      processUserApproval(approval, userDataMap);
    }
    // Handle multi-user approvals (with children)
    else if (approval.children) {
      approval.children.forEach((child) => {
        if (isEnrichedSpiffApprovalUser(child)) {
          processUserApproval(child, userDataMap);
        }
      });
    }
  });

  // Convert map values to array
  return Array.from(userDataMap.values());
};

/**
 * Generates CSV content from user data
 * @param data - User CSV data to export
 * @param isSpiffOnly - Whether the business is spiff-only (hides referral/signup columns)
 * @returns CSV string content
 */
export const generateCsvContent = (
  data: UserCsvData[],
  isSpiffOnly: boolean = false,
): string => {
  const visibleColumns = getVisibleColumns(isSpiffOnly);

  // Extract headers from visible columns
  const headers = visibleColumns.map((col) => col.header);

  // Map each user to a row of values using column accessors
  const rows = data.map((user) =>
    visibleColumns.map((col) => {
      const value = col.accessor(user);
      const formatted = col.formatter ? col.formatter(value) : String(value);
      return formatted;
    }),
  );

  // Escape and join CSV rows
  return [headers, ...rows]
    .map((row) =>
      row
        .map((field) => `"${String(field || "").replace(/"/g, '""')}"`)
        .join(","),
    )
    .join("\n");
};
